import React, { useState } from 'react';
import { ArrowLeft, Upload, X, FileText, Image, File, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import api from '../services/api';

interface SubmittedFile {
  file: File;
  preview?: string;
}

const SubmitProjectPage: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    message: ''
  });
  
  const [files, setFiles] = useState<SubmittedFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submittedProject, setSubmittedProject] = useState<any>(null);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(event.target.files || []);
    
    selectedFiles.forEach(file => {
      const newFile: SubmittedFile = { file };
      
      // Créer un aperçu pour les images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          newFile.preview = e.target?.result as string;
          setFiles(prev => [...prev, newFile]);
        };
        reader.readAsDataURL(file);
      } else {
        setFiles(prev => [...prev, newFile]);
      }
    });
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return <Image className="w-5 h-5" />;
    if (file.type.includes('pdf')) return <FileText className="w-5 h-5" />;
    return <File className="w-5 h-5" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);

      // Validation
      if (!formData.title.trim()) {
        setSubmitError('Le titre du projet est obligatoire');
        return;
      }

      if (!formData.description.trim()) {
        setSubmitError('La description du projet est obligatoire');
        return;
      }

      // Préparer les données pour l'envoi
      const submitData = new FormData();
      submitData.append('title', formData.title);
      submitData.append('description', formData.description);
      submitData.append('message', formData.message);

      // Ajouter les fichiers
      files.forEach(({ file }) => {
        submitData.append('files', file);
      });

      // Vérifier l'authentification
      if (!isAuthenticated) {
        setSubmitError('Vous devez être connecté pour soumettre un projet');
        return;
      }

      // Envoyer la requête
      const response = await api.post('/api/submitted-projects/', submitData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      const data = response.data;

      if (data.success) {
        setSubmittedProject(data.project);
        setSubmitted(true);
      } else {
        setSubmitError(data.error || 'Erreur lors de la soumission du projet');
      }

    } catch (error: any) {
      console.error('Erreur lors de la soumission:', error);
      setSubmitError('Erreur de connexion. Veuillez réessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-2xl mx-auto">
            
            {/* Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Projet Soumis avec Succès ! 🎉
              </h1>
              <p className="text-gray-600">Votre projet a été transmis à notre équipe</p>
            </div>

            {/* Confirmation */}
            <div className="bg-white rounded-2xl p-8 shadow-lg mb-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">
                  Merci pour votre soumission !
                </h2>
                <p className="text-gray-600">
                  Notre équipe va examiner votre projet et vous contactera bientôt.
                </p>
              </div>

              {/* Détails du projet soumis */}
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <h3 className="font-semibold text-gray-900 mb-4">📋 Détails de votre projet :</h3>
                <div className="space-y-3">
                  <div>
                    <span className="text-sm font-medium text-gray-600">Titre :</span>
                    <p className="text-gray-900">{submittedProject?.title}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">Description :</span>
                    <p className="text-gray-900">{submittedProject?.description}</p>
                  </div>
                  {submittedProject?.message && (
                    <div>
                      <span className="text-sm font-medium text-gray-600">Message :</span>
                      <p className="text-gray-900">{submittedProject.message}</p>
                    </div>
                  )}
                  <div>
                    <span className="text-sm font-medium text-gray-600">Fichiers joints :</span>
                    <p className="text-gray-900">{submittedProject?.files_count || 0} fichier(s)</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-gray-600">Statut :</span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      {submittedProject?.status_display}
                    </span>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => navigate('/')}
                  className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                  Retour à l'accueil
                </button>
                <button
                  onClick={() => navigate('/projets')}
                  className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                >
                  Voir mes projets
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          
          {/* Header */}
          <div className="flex items-center mb-8">
            <button
              onClick={() => navigate('/')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Soumettre un Projet</h1>
              <p className="text-gray-600 mt-1">Partagez votre idée de projet avec notre équipe</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            
            {/* Formulaire principal */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Informations du projet */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h2 className="text-xl font-bold text-gray-900 mb-6">📝 Informations du Projet</h2>
                
                <div className="space-y-6">
                  {/* Titre */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Titre du projet *
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => handleInputChange('title', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Ex: Application mobile pour restaurant"
                      required
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description détaillée *
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-32 resize-none"
                      placeholder="Décrivez votre projet en détail : objectifs, fonctionnalités souhaitées, public cible..."
                      required
                    />
                  </div>

                  {/* Message additionnel */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      💬 Message additionnel
                    </label>
                    <p className="text-xs text-gray-500 mb-3">
                      Ajoutez des informations sur votre budget, délais, contraintes techniques, etc.
                    </p>
                    <textarea
                      value={formData.message}
                      onChange={(e) => handleInputChange('message', e.target.value)}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-24 resize-none"
                      placeholder="Budget approximatif, délais souhaités, technologies préférées..."
                    />
                  </div>
                </div>
              </div>

              {/* Upload de fichiers */}
              <div className="bg-white rounded-2xl p-6 shadow-lg">
                <h2 className="text-xl font-bold text-gray-900 mb-6">📎 Fichiers Joints (Optionnel)</h2>
                
                {/* Zone de drop */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center mb-6">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Glissez vos fichiers ici
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Ou cliquez pour sélectionner des fichiers
                  </p>
                  <input
                    type="file"
                    multiple
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                    accept="image/*,.pdf,.doc,.docx,.txt"
                  />
                  <label
                    htmlFor="file-upload"
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Choisir des fichiers
                  </label>
                  <p className="text-xs text-gray-500 mt-2">
                    Images, PDF, documents (max 10MB par fichier)
                  </p>
                </div>

                {/* Liste des fichiers */}
                {files.length > 0 && (
                  <div className="space-y-3">
                    <h4 className="font-medium text-gray-900">Fichiers sélectionnés :</h4>
                    {files.map((fileData, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3">
                          {fileData.preview ? (
                            <img src={fileData.preview} alt="Preview" className="w-10 h-10 object-cover rounded" />
                          ) : (
                            <div className="w-10 h-10 bg-gray-200 rounded flex items-center justify-center">
                              {getFileIcon(fileData.file)}
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-medium text-gray-900">{fileData.file.name}</p>
                            <p className="text-xs text-gray-500">{formatFileSize(fileData.file.size)}</p>
                          </div>
                        </div>
                        <button
                          onClick={() => removeFile(index)}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              
              {/* Message pour utilisateurs non connectés */}
              {!isAuthenticated && (
                <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-yellow-800">
                      Veuillez vous connecter{' '}
                      <button
                        onClick={() => navigate('/login')}
                        className="text-blue-600 font-semibold underline hover:text-blue-800 transition-colors"
                      >
                        ici
                      </button>
                      {' '}pour soumettre un projet.
                    </p>
                  </div>
                </div>
              )}

              {/* Erreur */}
              {submitError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 text-sm">{submitError}</p>
                </div>
              )}

              {/* Bouton de soumission */}
              <button
                onClick={handleSubmit}
                disabled={!isAuthenticated || isSubmitting || !formData.title.trim() || !formData.description.trim()}
                className="w-full bg-blue-600 text-white px-6 py-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors font-medium flex items-center justify-center space-x-2"
                title={!isAuthenticated ? 'Veuillez vous connecter pour soumettre un projet' : ''}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Soumission en cours...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Soumettre le Projet</span>
                  </>
                )}
              </button>

              {/* Info */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">ℹ️ À savoir</h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Notre équipe examinera votre projet</li>
                  <li>• Vous recevrez une réponse sous 48h</li>
                  <li>• Un devis personnalisé vous sera proposé</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubmitProjectPage;
